/**
 * Top-bar items a role can hide. Language, the avatar menu and Support stay on
 * for every role, so they are not in this list.
 *
 * Mirror of TopNavItems in Wilma.Admin.Api (Rubble.Core/Authentication/Models/TopNavItems.cs).
 * The claim is the hidden ids, comma-separated, in TOP_NAV_IDS order. Empty or
 * absent means the whole configurable bar is shown — roles and tokens from
 * before this setting existed must not lose the bar.
 */
export const TOP_NAV_IDS = [
  "search",
  "help",
  "notifications",
  "usage",
  "basket",
] as const;

export type TopNavId = (typeof TOP_NAV_IDS)[number];

export type TopNavVisibility = Record<TopNavId, boolean>;

export const TOP_NAV_CLAIM = "topNav";

export const TOP_NAV_LABELS: Record<TopNavId, string> = {
  search: "Search",
  help: "Help",
  notifications: "Notifications",
  usage: "Usage",
  basket: "Basket",
};

export function allVisibleTopNav(): TopNavVisibility {
  return TOP_NAV_IDS.reduce((acc, id) => {
    acc[id] = true;
    return acc;
  }, {} as TopNavVisibility);
}

/** Missing keys stay visible. Unknown keys are ignored. */
export function topNavFromRecord(
  record: Record<string, boolean> | null | undefined
): TopNavVisibility {
  const result = allVisibleTopNav();
  if (!record) return result;
  for (const id of TOP_NAV_IDS) {
    if (typeof record[id] === "boolean") result[id] = record[id];
  }
  return result;
}

/** Claim value written onto the user. Empty when nothing is hidden. */
export function hiddenTopNavClaim(visibility: TopNavVisibility): string {
  return TOP_NAV_IDS.filter((id) => !visibility[id]).join(",");
}

/** Absent or blank claim shows everything. Unknown tokens are ignored. */
export function topNavFromClaim(
  claim: string | null | undefined
): TopNavVisibility {
  const result = allVisibleTopNav();
  if (!claim?.trim()) return result;
  const hidden = new Set(
    claim
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
  );
  for (const id of TOP_NAV_IDS) {
    if (hidden.has(id)) result[id] = false;
  }
  return result;
}
